import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  FileText, CheckCircle2, AlertTriangle, Loader2, PenTool, 
  Trash2, Download, Home, Mail, Phone, MapPin, Calendar,
  PoundSterling, Clock, Shield
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const SignAgreement = ({ token: propToken }) => {
  const { token: paramToken } = useParams();
  const token = propToken || paramToken; // Use prop if provided, otherwise use URL param
  const navigate = useNavigate();
  const { toast } = useToast();
  const canvasRef = useRef(null);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [signed, setSigned] = useState(false);
  const [agreementData, setAgreementData] = useState(null);
  
  const [signerName, setSignerName] = useState('');
  const [signatureType, setSignatureType] = useState('draw'); // 'draw' or 'type'
  const [typedSignature, setTypedSignature] = useState('');
  const [isDrawing, setIsDrawing] = useState(false);
  const [hasSignature, setHasSignature] = useState(false);

  useEffect(() => {
    fetchAgreementData();
  }, [token]);

  useEffect(() => {
    if (canvasRef.current && signatureType === 'draw') {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.strokeStyle = '#1e40af';
      ctx.lineWidth = 2;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';
    }
  }, [signatureType, agreementData]);

  const fetchAgreementData = async () => {
    try {
      const response = await fetch(`${API_URL}/api/signature/${token}`);
      if (response.ok) {
        const data = await response.json();
        setAgreementData(data);
        setSignerName(data.tenant_name || '');
      } else if (response.status === 410) {
        setError('expired');
      } else if (response.status === 400) {
        setError('already_signed');
      } else {
        setError('not_found');
      }
    } catch (err) {
      setError('error');
    } finally {
      setLoading(false);
    }
  };

  // Canvas drawing functions
  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasSignature(true);
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearSignature = () => {
    if (signatureType === 'draw' && canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      setHasSignature(false);
    } else {
      setTypedSignature('');
    }
  };

  const getSignatureData = () => {
    if (signatureType === 'draw' && canvasRef.current) {
      return canvasRef.current.toDataURL('image/png');
    } else if (signatureType === 'type' && typedSignature) {
      // Create a canvas with typed signature
      const canvas = document.createElement('canvas');
      canvas.width = 400;
      canvas.height = 100;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#1e40af';
      ctx.font = 'italic 32px "Brush Script MT", cursive';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);
      return canvas.toDataURL('image/png');
    }
    return null;
  };

  const handleSubmit = async () => {
    const signatureData = getSignatureData();
    
    if (signatureType === 'draw' && !hasSignature) {
      toast({ title: "Please draw your signature", variant: "destructive" });
      return;
    }
    
    if (signatureType === 'type' && !typedSignature.trim()) {
      toast({ title: "Please type your signature", variant: "destructive" });
      return;
    }
    
    if (!signerName.trim()) {
      toast({ title: "Please enter your full name", variant: "destructive" });
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await fetch(`${API_URL}/api/signature/${token}/sign`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          signature: signatureData,
          signer_name: signerName
        })
      });
      
      if (response.ok) {
        setSigned(true);
        toast({ title: "Agreement Signed Successfully!" });
      } else {
        const error = await response.json();
        toast({ title: "Error", description: error.detail, variant: "destructive" });
      }
    } catch (err) {
      toast({ title: "Error signing agreement", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  // Error states
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#FF8C42] mx-auto mb-4" />
          <p className="text-gray-600">Loading agreement...</p>
        </div>
      </div>
    );
  }

  if (error === 'expired') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <Clock className="w-16 h-16 text-orange-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Link Expired</h1>
            <p className="text-gray-600 mb-6">This signing link has expired. Please contact Harry & Jacobs to request a new signing link.</p>
            <Button onClick={() => navigate('/')} className="bg-[#FF8C42] hover:bg-[#FF6B35]">
              <Home className="w-4 h-4 mr-2" /> Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error === 'already_signed') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <CheckCircle2 className="w-16 h-16 text-green-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Already Signed</h1>
            <p className="text-gray-600 mb-6">This agreement has already been signed. A copy was sent to your email.</p>
            <Button onClick={() => navigate('/')} className="bg-[#FF8C42] hover:bg-[#FF6B35]">
              <Home className="w-4 h-4 mr-2" /> Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold mb-2">Agreement Not Found</h1>
            <p className="text-gray-600 mb-6">We couldn't find this agreement. Please check your email for the correct link.</p>
            <Button onClick={() => navigate('/')} className="bg-[#FF8C42] hover:bg-[#FF6B35]">
              <Home className="w-4 h-4 mr-2" /> Return Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Success state
  if (signed) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <Card className="max-w-lg w-full">
          <CardContent className="p-8 text-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle2 className="w-12 h-12 text-green-500" />
            </div>
            <h1 className="text-2xl font-bold text-green-700 mb-2">Agreement Signed Successfully!</h1>
            <p className="text-gray-600 mb-6">
              Thank you for signing your tenancy agreement. A confirmation email with the signed document has been sent to <strong>{agreementData?.tenant_email}</strong>.
            </p>
            <div className="bg-gray-50 p-4 rounded-lg mb-6 text-left">
              <p className="text-sm text-gray-600"><strong>Property:</strong> {agreementData?.property_address}</p>
              <p className="text-sm text-gray-600"><strong>Signed by:</strong> {signerName}</p>
              <p className="text-sm text-gray-600"><strong>Date:</strong> {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}</p>
            </div>
            <div className="flex gap-3 justify-center">
              <Button 
                onClick={() => window.open(`${API_URL}/api/signature/${token}/download`, '_blank')}
                className="bg-[#FF8C42] hover:bg-[#FF6B35]"
              >
                <Download className="w-4 h-4 mr-2" /> Download Signed Agreement
              </Button>
              <Button variant="outline" onClick={() => navigate('/')}>
                <Home className="w-4 h-4 mr-2" /> Return Home
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Main signing interface
  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-[#404040] text-white py-4 px-6">
        <div className="max-w-4xl mx-auto flex items-center gap-4">
          <FileText className="w-8 h-8 text-[#FF8C42]" />
          <div>
            <h1 className="text-xl font-bold">Harry & Jacobs</h1>
            <p className="text-sm text-gray-300">E-Signature Portal</p>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 md:p-6">
        {/* Important Notice Banner */}
        <div className="bg-amber-50 border-l-4 border-amber-500 p-4 mb-6 rounded-r-lg">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-bold text-amber-800">COMPULSORY SIGNATURE REQUIRED</h3>
              <p className="text-amber-700 text-sm mt-1">
                This document <strong>MUST be signed</strong> to validate your tenancy agreement. 
                Your tenancy cannot be confirmed until this agreement has been signed and returned.
              </p>
            </div>
          </div>
        </div>

        {/* Agreement Details Card */}
        <Card className="mb-6 shadow-lg">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-[#FF8C42]" />
              Tenancy Agreement Details
            </h2>
            
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-700">
                  <MapPin className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">Property:</span>
                  <span>{agreementData?.property_address || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <PoundSterling className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">Monthly Rent:</span>
                  <span>£{agreementData?.monthly_rent?.toLocaleString() || 'N/A'}</span>
                </div>
                {agreementData?.tenant_details?.tenancy_start && (
                  <div className="flex items-center gap-2 text-gray-700">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span className="font-medium">Start Date:</span>
                    <span>{agreementData.tenant_details.tenancy_start}</span>
                  </div>
                )}
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-gray-700">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">Tenant:</span>
                  <span>{agreementData?.tenant_name || 'N/A'}</span>
                </div>
                <div className="flex items-center gap-2 text-gray-700">
                  <Mail className="w-4 h-4 text-gray-400" />
                  <span className="font-medium">Email:</span>
                  <span>{agreementData?.tenant_email || 'N/A'}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Terms Summary */}
        <Card className="mb-6 shadow-lg">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Key Terms</h2>
            <ul className="space-y-2 text-gray-700">
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                <span>Rent is due on the agreed date each month</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                <span>Tenant is responsible for keeping the property in good condition</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                <span>Deposit is protected under an approved scheme</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                <span>One month's notice required to terminate after fixed term</span>
              </li>
              <li className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-500 mt-1 flex-shrink-0" />
                <span>Tenant is responsible for utility bills during the tenancy</span>
              </li>
            </ul>
          </CardContent>
        </Card>

        {/* Signature Section */}
        <Card className="shadow-lg border-2 border-[#FF8C42]">
          <CardContent className="p-6">
            <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
              <PenTool className="w-5 h-5 text-[#FF8C42]" />
              Sign Agreement
            </h2>

            {/* Full Name */}
            <div className="mb-6">
              <Label htmlFor="signer_name" className="text-gray-700 font-medium">
                Your Full Legal Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="signer_name"
                value={signerName}
                onChange={(e) => setSignerName(e.target.value)}
                placeholder="Enter your full name as it appears on your ID"
                className="mt-2"
              />
            </div>

            {/* Signature Type Toggle */}
            <div className="mb-4">
              <Label className="text-gray-700 font-medium mb-2 block">
                Signature Method <span className="text-red-500">*</span>
              </Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={signatureType === 'draw' ? 'default' : 'outline'}
                  onClick={() => setSignatureType('draw')}
                  className={signatureType === 'draw' ? 'bg-[#FF8C42] hover:bg-[#FF6B35]' : ''}
                >
                  <PenTool className="w-4 h-4 mr-2" /> Draw Signature
                </Button>
                <Button
                  type="button"
                  variant={signatureType === 'type' ? 'default' : 'outline'}
                  onClick={() => setSignatureType('type')}
                  className={signatureType === 'type' ? 'bg-[#FF8C42] hover:bg-[#FF6B35]' : ''}
                >
                  <FileText className="w-4 h-4 mr-2" /> Type Signature
                </Button>
              </div>
            </div>

            {/* Signature Input */}
            {signatureType === 'draw' ? (
              <div className="mb-4">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-2 bg-white">
                  <canvas
                    ref={canvasRef}
                    width={500}
                    height={150}
                    className="w-full touch-none cursor-crosshair"
                    style={{ maxWidth: '100%', height: 'auto' }}
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                  />
                </div>
                <p className="text-sm text-gray-500 mt-2">Draw your signature above using your mouse or finger</p>
              </div>
            ) : (
              <div className="mb-4">
                <Input
                  value={typedSignature}
                  onChange={(e) => setTypedSignature(e.target.value)}
                  placeholder="Type your full name"
                  className="text-2xl h-16"
                  style={{ fontFamily: '"Brush Script MT", cursive' }}
                />
                {typedSignature && (
                  <div className="mt-3 p-4 bg-gray-50 rounded-lg border text-center">
                    <p className="text-sm text-gray-500 mb-2">Preview:</p>
                    <p className="text-3xl text-blue-800" style={{ fontFamily: '"Brush Script MT", cursive' }}>
                      {typedSignature}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Clear Button */}
            <Button variant="outline" onClick={clearSignature} className="mb-6">
              <Trash2 className="w-4 h-4 mr-2" /> Clear Signature
            </Button>

            {/* Legal Disclaimer */}
            <div className="bg-gray-50 p-4 rounded-lg mb-6 text-sm text-gray-600">
              <div className="flex items-start gap-2">
                <Shield className="w-5 h-5 text-gray-400 flex-shrink-0 mt-0.5" />
                <p>
                  By clicking "Sign Agreement" below, I confirm that I have read and agree to all terms and conditions 
                  of this Tenancy Agreement. I understand that this electronic signature is legally binding and has 
                  the same legal effect as a handwritten signature.
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <Button
              onClick={handleSubmit}
              disabled={submitting}
              className="w-full bg-green-600 hover:bg-green-700 h-14 text-lg font-bold"
              data-testid="sign-agreement-btn"
            >
              {submitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Processing...
                </>
              ) : (
                <>
                  <CheckCircle2 className="w-5 h-5 mr-2" /> Sign Agreement
                </>
              )}
            </Button>
          </CardContent>
        </Card>

        {/* Footer */}
        <div className="text-center mt-6 text-sm text-gray-500">
          <p>Harry & Jacobs Estate Agents | Harrow, London</p>
          <p className="mt-1">Questions? Contact us at info@harryandjacobs.com</p>
        </div>
      </main>
    </div>
  );
};

export default SignAgreement;
